/**
 * AuthGate — Singleton request coordinator for token refresh.
 *
 * Sits between the client API and the network. Handles auth in two modes:
 *
 * **Proactive**: Before each request, checks if the token is expiring soon
 * (within REFRESH_BUFFER_MS). If so, refreshes first — the caller never
 * sees a 401.
 *
 * **Reactive**: If a 401 arrives anyway (e.g. key rotation, revocation),
 * the first caller takes ownership of the refresh, subsequent callers
 * queue, and all retry or reject together.
 *
 * Concurrency: only one refresh is ever in flight. All other callers
 * wait on the same promise.
 */

import { handleAuthError } from '@/lib/hooks/use-auth-error-handler';
import { isAuthenticationError } from '@/lib/hooks/use-auth-error-handler';
import {
  getCachedSession,
  invalidateSessionCache
} from '@/lib/api/session-cache';
import {
  ACCOUNT_NOT_PROVISIONED_CODE,
  ACCOUNT_NOT_PROVISIONED_ERROR,
  type FederatedLogoutReason,
  forceFederatedLogout,
  SESSION_INVALID_ERROR
} from '@/lib/auth/federated-logout';

type GateState = 'active' | 'refreshing' | 'failed';

/** Shape of ActionResult returned by server actions. */
interface ActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
  code?: string;
}

/**
 * Read the backend's machine-readable auth `code` off a 401 body and map it to
 * the `?error=` the /login page renders. An unprovisioned account gets its own
 * card, because the generic "sign in again" advice would only loop the user
 * back to the same failure (LF-5950). Body parsing is best-effort — any
 * unreadable or unrecognised body falls back to the generic card.
 */
async function loginErrorForResponse(
  response: Response
): Promise<FederatedLogoutReason> {
  try {
    const body = (await response.clone().json()) as { code?: unknown };
    if (body?.code === ACCOUNT_NOT_PROVISIONED_CODE) {
      return ACCOUNT_NOT_PROVISIONED_ERROR;
    }
  } catch {
    // Non-JSON or already-consumed body — fall through to the generic card.
  }
  return SESSION_INVALID_ERROR;
}

interface Waiter {
  resolve: (refreshed: boolean) => void;
}

/** Refresh proactively when token expires within this window (ms). */
const REFRESH_BUFFER_MS = 30_000;

/** Minimum time to stay in `failed` state before auto-resetting (ms). */
const FAILED_RESET_MS = 10_000;

class AuthGate {
  private state: GateState = 'active';
  private refreshPromise: Promise<boolean> | null = null;
  private waiters: Waiter[] = [];
  private failedTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Token expiry as a Unix timestamp in seconds.
   * `null` means unknown — we'll bootstrap it on first request.
   */
  private expiresAt: number | null = null;
  private bootstrapped = false;

  /**
   * Wrap a fetch call. The gate will:
   * 1. Proactively refresh if the token is expiring soon
   * 2. Execute the request
   * 3. Reactively refresh + retry if a 401 comes back anyway
   */
  async gate(requestFn: () => Promise<Response>): Promise<Response> {
    // If refresh failed and redirect is in progress, reject immediately
    if (this.state === 'failed') {
      throw new AuthGateError('Session expired, redirecting to login');
    }

    // If a refresh is already in progress, wait for it before executing
    if (this.state === 'refreshing') {
      const refreshed = await this.enqueue();
      if (!refreshed) {
        throw new AuthGateError('Session expired, redirecting to login');
      }
      return requestFn();
    }

    // Bootstrap: fetch current expiry once on first request
    if (!this.bootstrapped) {
      await this.bootstrap();
    }

    // Proactive refresh: if token expires within the buffer, refresh first
    if (this.isExpiringSoon()) {
      const refreshed = await this.coordinateRefresh();
      if (!refreshed) {
        throw new AuthGateError('Session expired, redirecting to login');
      }
    }

    // Execute the request
    const response = await requestFn();

    if (response.status !== 401) {
      return response;
    }

    // Got 401 despite proactive check — token was invalidated server-side
    // (key rotation, revocation, etc.). Attempt reactive refresh + retry.
    return this.handleUnauthorized(requestFn);
  }

  // ── Proactive refresh ──────────────────────────────────────────────

  /** Check if the token is expiring within the buffer window. */
  private isExpiringSoon(): boolean {
    if (this.expiresAt === null) return false;
    const nowSec = Date.now() / 1000;
    return this.expiresAt - nowSec < REFRESH_BUFFER_MS / 1000;
  }

  /** Fetch current token expiry from the server (one-time on first request). */
  private async bootstrap() {
    this.bootstrapped = true;
    try {
      const res = await fetch('/api/auth/refresh', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.expiresAt) {
          this.expiresAt = data.expiresAt;
        }
      }
    } catch {
      // Non-fatal — we'll fall back to reactive refresh
    }
  }

  // ── Reactive refresh ───────────────────────────────────────────────

  /**
   * Called by the first request that encounters a 401.
   * Takes ownership of the refresh; other callers wait.
   */
  private async handleUnauthorized(
    retryFn: () => Promise<Response>
  ): Promise<Response> {
    // Another request may have started refreshing between our fetch
    // and arriving here
    if (this.state === 'refreshing') {
      const refreshed = await this.enqueue();
      if (!refreshed) {
        throw new AuthGateError('Session expired, redirecting to login');
      }
      return this.retryOrForceLogout(retryFn);
    }

    if (this.state === 'failed') {
      throw new AuthGateError('Session expired, redirecting to login');
    }

    const success = await this.coordinateRefresh();
    if (success) {
      return this.retryOrForceLogout(retryFn);
    }

    throw new AuthGateError('Session expired, redirecting to login');
  }

  /**
   * Retry the request after a SUCCESSFUL refresh. If the retry still returns
   * 401, the token is valid (refresh worked) yet the backend rejects it — an
   * UNRECOVERABLE session (e.g. no `tenant_id`). Force a full federated logout
   * so the Keycloak SSO cookie can't silently re-mint the broken session, and
   * reject the request. (LF-4870.)
   */
  private async retryOrForceLogout(
    retryFn: () => Promise<Response>
  ): Promise<Response> {
    const retried = await retryFn();
    if (retried.status === 401) {
      await this.triggerUnrecoverableLogout(
        await loginErrorForResponse(retried)
      );
      throw new AuthGateError('Session unrecoverable, signing out');
    }
    return retried;
  }

  /**
   * Fetch the id_token (for the Keycloak end-session hint) and force a full
   * federated logout. Move the gate to `failed` so in-flight/queued callers
   * reject promptly while the browser navigates away.
   */
  private async triggerUnrecoverableLogout(
    loginError: FederatedLogoutReason = SESSION_INVALID_ERROR
  ): Promise<void> {
    this.state = 'failed';
    this.scheduleFailedReset();
    this.resolveWaiters(false);

    let idToken: string | undefined;
    try {
      const session = await getCachedSession();
      idToken = session?.idToken;
    } catch {
      // Non-fatal — the federated logout still proceeds without the hint.
    }
    await forceFederatedLogout(idToken, loginError);
  }

  /**
   * Like handleUnauthorized but for server actions returning ActionResult.
   */
  private async handleActionUnauthorized<T>(
    retryFn: () => Promise<ActionResult<T>>
  ): Promise<ActionResult<T>> {
    if (this.state === 'refreshing') {
      const refreshed = await this.enqueue();
      if (!refreshed) {
        return {
          success: false,
          error: 'Session expired, redirecting to login'
        };
      }
      return this.retryActionOrForceLogout(retryFn);
    }

    const success = await this.coordinateRefresh();
    if (success) {
      return this.retryActionOrForceLogout(retryFn);
    }

    return { success: false, error: 'Session expired, redirecting to login' };
  }

  /**
   * Server-action counterpart of `retryOrForceLogout`: retry after a
   * successful refresh, and if the action STILL returns an auth error the
   * session is unrecoverable — force a full federated logout (LF-4870).
   */
  private async retryActionOrForceLogout<T>(
    retryFn: () => Promise<ActionResult<T>>
  ): Promise<ActionResult<T>> {
    const retried = await retryFn();
    if (!retried.success && isAuthenticationError(retried.error)) {
      await this.triggerUnrecoverableLogout();
      return { success: false, error: 'Session unrecoverable, signing out' };
    }
    return retried;
  }

  // ── Shared refresh coordination ────────────────────────────────────

  /**
   * Coordinate a single refresh across all callers.
   * If a refresh is already in progress, piggyback on it.
   * Returns true if refresh succeeded.
   */
  private async coordinateRefresh(): Promise<boolean> {
    if (this.state === 'refreshing') {
      return this.enqueue();
    }

    this.state = 'refreshing';

    this.refreshPromise = this.attemptRefresh();
    const success = await this.refreshPromise;
    this.refreshPromise = null;

    if (success) {
      this.state = 'active';
      this.resolveWaiters(true);
      return true;
    }

    this.state = 'failed';
    this.resolveWaiters(false);
    this.scheduleFailedReset();
    handleAuthError({ reason: 'token_refresh_failed' });
    return false;
  }

  /**
   * Call the refresh endpoint. Returns true if refresh succeeded.
   * On success, updates the stored expiresAt.
   */
  private async attemptRefresh(): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.expiresAt) {
          this.expiresAt = data.expiresAt;
        }
        // Clear cached session so the next API call fetches fresh tokens
        invalidateSessionCache();
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // ── Waiter queue ───────────────────────────────────────────────────

  /** Wait for the in-flight refresh to complete. */
  private enqueue(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    return new Promise<boolean>((resolve) => {
      this.waiters.push({ resolve });
    });
  }

  private resolveWaiters(refreshed: boolean) {
    const pending = this.waiters.splice(0);
    for (const w of pending) {
      w.resolve(refreshed);
    }
  }

  // ── Server action support ───────────────────────────────────────────

  /**
   * Wrap a server action call. Like `gate()` but for server actions that
   * return `ActionResult<T>` instead of `Response`.
   *
   * - Proactive refresh before the call if token is expiring
   * - If the action returns an auth error, refresh + retry once
   * - Concurrent callers share the same refresh
   */
  async gateAction<T>(
    actionFn: () => Promise<ActionResult<T>>
  ): Promise<ActionResult<T>> {
    if (this.state === 'failed') {
      return {
        success: false,
        error: 'Session expired, redirecting to login'
      };
    }

    if (this.state === 'refreshing') {
      const refreshed = await this.enqueue();
      if (!refreshed) {
        return {
          success: false,
          error: 'Session expired, redirecting to login'
        };
      }
      return actionFn();
    }

    if (!this.bootstrapped) {
      await this.bootstrap();
    }

    // Proactive refresh
    if (this.isExpiringSoon()) {
      const refreshed = await this.coordinateRefresh();
      if (!refreshed) {
        return {
          success: false,
          error: 'Session expired, redirecting to login'
        };
      }
    }

    const result = await actionFn();

    // Check if the result is an auth error — refresh + retry
    if (!result.success && isAuthenticationError(result.error)) {
      return this.handleActionUnauthorized(actionFn);
    }

    return result;
  }

  // ── Recovery ───────────────────────────────────────────────────────

  /**
   * Auto-reset from `failed` after a timeout.
   * Prevents permanent lockout if the user navigates back from login.
   */
  private scheduleFailedReset() {
    if (this.failedTimer) clearTimeout(this.failedTimer);
    this.failedTimer = setTimeout(() => {
      if (this.state === 'failed') {
        this.state = 'active';
        this.expiresAt = null;
        this.bootstrapped = false;
      }
      this.failedTimer = null;
    }, FAILED_RESET_MS);
  }

  /** Reset the gate to active (call after successful login). */
  reset() {
    this.state = 'active';
    this.expiresAt = null;
    this.bootstrapped = false;
    if (this.failedTimer) {
      clearTimeout(this.failedTimer);
      this.failedTimer = null;
    }
  }
}

/** Error thrown when the gate rejects a request due to auth failure. */
export class AuthGateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthGateError';
  }
}

/** Singleton instance. */
export const authGate = new AuthGate();

/**
 * Wrap a server action call with auth-aware retry.
 *
 * Use this in React Query `queryFn` callbacks to get the same proactive
 * refresh + reactive retry behaviour that `directApiClient` gets via `gate()`.
 *
 * @example
 * ```ts
 * const query = useQuery({
 *   queryKey: ['profile'],
 *   queryFn: () => gatedAction(() => getUserProfileAction()),
 * });
 * ```
 */
export async function gatedAction<T>(
  actionFn: () => Promise<ActionResult<T>>
): Promise<T> {
  const result = await authGate.gateAction(actionFn);

  if (!result.success) {
    // Carry the HTTP status onto the thrown error so React Query's retry
    // predicate (`lib/providers/query-provider.tsx`) can recognise a 4xx and
    // skip the retry, instead of retrying a call that can never succeed.
    const error = new Error(result.error || 'Action failed') as Error & {
      status?: number;
    };
    if (result.statusCode !== undefined) {
      error.status = result.statusCode;
    }
    throw error;
  }

  return result.data as T;
}
